import React from "react";
import { AlertTriangle, ShieldCheck, X } from "lucide-react";

/**
 * PII detection banner. Rendered above the upload success message.
 * Given a `pii_report` object returned by the backend, shows:
 *   - a WARNING if PII detected (with counts + explicit "NOT sent to AI")
 *   - a GREEN confirmation if no PII detected
 *
 * Props:
 *   report — the `pii_report` field from the upload response
 *   onDismiss — optional close handler
 */
export default function PiiWarningBanner({ report, onDismiss }) {
  if (!report) return null;

  const nameCount = report.name_count || 0;
  const phoneCount = report.phone_count || 0;
  const hasPii = !!report.has_pii;
  const nameCols = report.name_columns || [];
  const phoneCols = report.phone_columns || [];
  const samples = report.sample_masked || {};

  if (!hasPii) {
    return (
      <div
        className="rounded-lg border border-green-500/40 bg-green-500/10 p-4 mb-4 flex items-start gap-3"
        data-testid="pii-banner-clean"
      >
        <ShieldCheck className="text-green-400 flex-shrink-0 mt-0.5" size={20} />
        <div className="flex-1">
          <div className="text-green-300 font-semibold">No PII detected in this upload.</div>
          <div className="text-green-200/80 text-sm mt-1">
            No customer names or phone numbers were found. As always, uploaded data is stored only in this application &mdash; nothing is sent to any AI/LLM service.
          </div>
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="text-green-200/60 hover:text-green-200" aria-label="Dismiss">
            <X size={16} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-4 mb-4"
      data-testid="pii-banner-warning"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="text-amber-400 flex-shrink-0 mt-0.5" size={22} />
        <div className="flex-1">
          <div className="text-amber-200 font-semibold">
            PII detected in this upload
            {nameCount ? ` — ${nameCount} name${nameCount === 1 ? "" : "s"}` : ""}
            {phoneCount ? `${nameCount ? "," : " —"} ${phoneCount} phone number${phoneCount === 1 ? "" : "s"}` : ""}
          </div>
          <div className="text-amber-100/90 text-sm mt-1.5">
            This data is stored securely in the application database.
            <strong className="text-amber-100"> No customer names or phone numbers are sent to any AI/LLM service</strong>
            {" "}(no OpenAI, Anthropic, Google Gemini, or 3rd-party AI processor).
          </div>
          {(nameCols.length > 0 || phoneCols.length > 0) && (
            <details className="mt-3 group" data-testid="pii-details">
              <summary className="cursor-pointer text-amber-200 text-sm underline decoration-dotted hover:text-amber-100">
                View detected columns &amp; masked samples
              </summary>
              <div className="mt-2 space-y-3 text-sm">
                {nameCols.length > 0 && (
                  <div>
                    <div className="text-amber-200/90 font-medium mb-1">Name columns ({nameCols.length}):</div>
                    <ul className="ml-4 space-y-1">
                      {nameCols.map((c) => (
                        <li key={c} className="text-amber-100/80">
                          <span className="font-mono">{c}</span>
                          {samples[c] && (
                            <span className="ml-2 text-amber-200/60">
                              e.g. {samples[c].join(", ")}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {phoneCols.length > 0 && (
                  <div>
                    <div className="text-amber-200/90 font-medium mb-1">Phone-number columns ({phoneCols.length}):</div>
                    <ul className="ml-4 space-y-1">
                      {phoneCols.map((c) => (
                        <li key={c} className="text-amber-100/80">
                          <span className="font-mono">{c}</span>
                          {samples[c] && (
                            <span className="ml-2 text-amber-200/60">
                              e.g. {samples[c].join(", ")}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </details>
          )}
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="text-amber-200/60 hover:text-amber-200" aria-label="Dismiss">
            <X size={18} />
          </button>
        )}
      </div>
    </div>
  );
}
